<script setup lang="ts">
import { ref, computed } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import axios from "axios"
import { useI18n } from 'vue-i18n';
import { useToast } from "@/components/ui/toast"

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'

import { Settings} from "lucide-vue-next";


const { t } = useI18n();
const { toast } = useToast();

const columnSearch = ref("")

const filteredColumns = computed(() => {
  const q = columnSearch.value.toLowerCase().trim()
  const cols = props.columns.filter(c => c.key !== 'actions' && c.key !== 'buffer')
  if (!q) return cols
  return cols.filter(c => c.label?.toLowerCase().includes(q))
})

const props = withDefaults(defineProps<{
  columns: ColumnDef[]
  endpoint?: string
  enableAll?: boolean
  ownColumnKeys?: string[]
}>(), {
  enableAll: false,
  ownColumnKeys: () => []
})


const emit = defineEmits<{
  (e: "toggleColumn"): void;
}>();


async function persistColumnToggle(col) {
  if (!props.endpoint) return
  try {
    await axios.post(`/${props.endpoint}/togglecol`, {
      column: col.key,
      show: col.show
    });
  } catch (error: any) {
    const message =
      error.response?.data?.message ||
      error.response?.statusText ||
      `Failed to update ${col.label}`;
    toast({ title: "Error", description: message, variant: "destructive" });
  }
}

async function toggleColumn(col, withEmit = true) {
  console.log("toggleColumn col=" + col.key)
  await persistColumnToggle(col)
  // Tables without `endpoint` (e.g. assignment matrix with only `:data`) still need this so
  // DataTable can persist column visibility via columnStore and refresh the grid.
  if (withEmit) {
    emit("toggleColumn");
  }
}

async function selectAllColumns() {
  for (const c of props.columns) {
    if (!c.show) {
      c.show = true
      await persistColumnToggle(c)
    }
  }
  emit("toggleColumn")
}

async function deselectAllColumns() {
  for (const c of props.columns) {
    if (c.show) {
      c.show = false
      await persistColumnToggle(c)
    }
  }
  emit("toggleColumn")
}

async function selectOwnColumns() {
  const ownSet = new Set(props.ownColumnKeys)
  for (const c of props.columns) {
    const shouldShow = ownSet.has(c.key)
    if (c.show !== shouldShow) {
      c.show = shouldShow
      await persistColumnToggle(c)
    }
  }
  emit("toggleColumn")
}
console.log("[ColumnSelect] ownColumnKeys=" + JSON.stringify(props.ownColumnKeys) + " columns=" + JSON.stringify(props.columns.map(c => c.key)))

</script>

<template>
<DropdownMenu>
  <DropdownMenuTrigger asChild>
    <Button variant="outline" size="sm" :title="t('view.columns_hs')">
      <Settings/> {{ t('view.columns') }}
    </Button>
  </DropdownMenuTrigger>

  <DropdownMenuContent class="w-60 max-h-80 z-[10001]" @closeAutoFocus.prevent>
    <DropdownMenuLabel>{{ t('view.columns_hs') }}</DropdownMenuLabel>

    <div class="px-2 pb-2">
      <Input
        v-model="columnSearch"
        :placeholder="t('view.filter_placeholder')"
        class="h-7 text-sm"
        @keydown.stop
      />
    </div>

    <DropdownMenuSeparator />

    <DropdownMenuItem v-if="props.enableAll"
      class="flex items-center gap-2 px-2 py-1 font-semibold text-blue-600 cursor-pointer"
      @click="selectAllColumns"
    >
      {{ t('view.all') }}
    </DropdownMenuItem>

    <DropdownMenuItem v-if="props.enableAll"
      class="flex items-center gap-2 px-2 py-1 font-semibold text-blue-600 cursor-pointer"
      @click="deselectAllColumns"
    >
      {{ t('view.none') }}
    </DropdownMenuItem>

    <DropdownMenuItem v-if="props.ownColumnKeys && props.ownColumnKeys.length > 0"
      class="flex items-center gap-2 px-2 py-1 font-semibold text-blue-600 cursor-pointer"
      @click="selectOwnColumns"
    >
      {{ t('view.own') }}
    </DropdownMenuItem>

    <DropdownMenuSeparator />

    <DropdownMenuGroup class="max-h-48 overflow-y-auto">
      <DropdownMenuItem
        v-for="c in filteredColumns"
        :key="c.key"
        class="flex items-center justify-between"
      >
        <label class="flex items-center gap-2 cursor-pointer w-full">
          <input 
            :disabled="!c.togglefilter"
            type="checkbox" 
            v-model="c.show" 
            class="accent-blue-500"
            @change="toggleColumn(c)"
          />
          <span> {{ c.label }} </span>
        </label>
      </DropdownMenuItem>
    </DropdownMenuGroup>
  </DropdownMenuContent>
</DropdownMenu>
</template>
